import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import { Avatar, Modal, ModalBody, ModalContent } from '@heroui/react';
import { CHAT_MODAL_WIDTH_CLASS } from '../../config/constants';
import { buildUserPresenceLabel } from '../../lib/formatParticipantPresence';
import { downloadChatAttachment, getChatAttachmentPreviewUrl } from '../../lib/attachmentLinks';
import {
  canPromoteGroupParticipantToAdmin,
  canRemoveGroupParticipant,
  canToggleParticipantAdminRole,
  formatParticipantRole,
} from '../../lib/groupChatPermissions';
import type {
  ChatAttachment,
  ChatDetail,
  ChatDetailAttachment,
  ChatMessage,
  ChatParticipant,
} from '../../model/types';
import { ChatMediaViewerModal } from '../ChatMediaViewerModal/ChatMediaViewerModal';
import {
  IconArrowLeft,
  IconChevronDown,
  IconClose,
  IconDownload,
  IconFile,
  IconImage,
  IconPin,
  IconPlay,
  IconTrash,
  IconUserPlus,
  IconUsers,
  IconVideo,
} from '../icons';

type ChatDetailsView = 'overview' | 'photos' | 'videos' | 'files' | 'pinned';

export type ChatDetailsModalProps = {
  isOpen: boolean;
  detail: ChatDetail | null;
  titleFallback: string;
  isLoading: boolean;
  errorMessage: string | null;
  view: ChatDetailsView;
  canManageGroup?: boolean;
  onEditGroup?: () => void;
  canLeave?: boolean;
  isLeavingChat?: boolean;
  leaveErrorMessage?: string | null;
  onLeaveChat?: () => void | Promise<void>;
  canDeleteGroup?: boolean;
  isDeletingChat?: boolean;
  deleteErrorMessage?: string | null;
  onDeleteGroup?: () => void | Promise<void>;
  currentUserId?: number;
  isStaffUser?: boolean;
  canToggleAdmin?: boolean;
  togglingAdminUserId?: number | null;
  adminToggleErrorMessage?: string | null;
  onToggleParticipantAdmin?: (userId: number) => void | Promise<void>;
  isArchived?: boolean;
  isTogglingArchive?: boolean;
  onToggleArchive?: () => void | Promise<void>;
  onClose: () => void;
  onBack: () => void;
  onOpenView: (view: Exclude<ChatDetailsView, 'overview'>) => void;
  pinnedMessages?: ChatMessage[];
  canManageParticipants?: boolean;
  onRemoveParticipant?: (userId: number) => void | Promise<void>;
  removingParticipantUserId?: number | null;
};

type AssetGroup = {
  label: string;
  items: ChatDetailAttachment[];
};

function groupAssets(items: ChatDetailAttachment[]): AssetGroup[] {
  const groups = new Map<string, ChatDetailAttachment[]>();

  items.forEach((item) => {
    const label = item.createdAt ? format(item.createdAt, 'MMMM yyyy') : 'Media';
    const current = groups.get(label) ?? [];
    current.push(item);
    groups.set(label, current);
  });

  return Array.from(groups.entries()).map(([label, groupItems]) => ({
    label,
    items: groupItems,
  }));
}

function getPinnedPreview(message: ChatMessage) {
  if (message.text?.trim()) {
    return message.text.trim();
  }

  if (message.attachments.length) {
    return `Attachment: ${message.attachments[0].name}`;
  }

  return 'Message';
}

function getParticipantJobTitle(participant: ChatParticipant) {
  return participant.user.badge?.trim() || 'Member';
}

function ParticipantRoleBadge({ role }: { role: string }) {
  const normalized = role.toLowerCase();
  if (normalized !== 'owner' && normalized !== 'admin') {
    return null;
  }

  return (
    <span className="shrink-0 rounded-[8px] bg-[#EFEFEF] px-2.5 py-1 text-[12px] font-medium leading-none text-[#5C5C5C]">
      {formatParticipantRole(role)}
    </span>
  );
}

type MediaRowProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
};

function MediaRow({ icon, label, onClick }: MediaRowProps) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-4 rounded-[18px] px-3 py-3 text-left transition-colors hover:bg-black/[0.04] active:bg-black/[0.06]"
      onClick={onClick}
    >
      <span className="text-[#1E1E1E]">{icon}</span>
      <span className="text-[16px] font-medium text-[#1E1E1E]">{label}</span>
    </button>
  );
}

const ChatDetailVideoThumbnail = memo(({ item }: { item: ChatDetailAttachment }) => {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const thumbnailUrl = item.thumbnailUrl && !thumbnailFailed ? item.thumbnailUrl : null;
  // Видео с сервера ссылки не несёт: кадр рисуется только из локального файла
  const localVideoUrl = item.isLocal ? item.url : null;

  return (
    <div className="aspect-square w-full bg-black/90">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setThumbnailFailed(true)}
        />
      ) : localVideoUrl && !videoFailed ? (
        <video
          src={localVideoUrl}
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          className="pointer-events-none h-full w-full object-cover"
          onError={() => setVideoFailed(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-black/80" />
      )}
    </div>
  );
});
ChatDetailVideoThumbnail.displayName = 'ChatDetailVideoThumbnail';

export const ChatDetailsModal = memo(
  ({
    isOpen,
    detail,
    titleFallback,
    isLoading,
    errorMessage,
    view,
    canManageGroup = false,
    onEditGroup,
    canLeave = false,
    isLeavingChat = false,
    leaveErrorMessage = null,
    onLeaveChat,
    canDeleteGroup = false,
    isDeletingChat = false,
    deleteErrorMessage = null,
    onDeleteGroup,
    currentUserId = 0,
    isStaffUser = false,
    canToggleAdmin = false,
    togglingAdminUserId = null,
    adminToggleErrorMessage = null,
    onToggleParticipantAdmin,
    isArchived = false,
    isTogglingArchive = false,
    onToggleArchive,
    onClose,
    onBack,
    onOpenView,
    pinnedMessages = [],
    canManageParticipants = false,
    onRemoveParticipant,
    removingParticipantUserId = null,
  }: ChatDetailsModalProps) => {
    const title = detail?.title || titleFallback;
    const participantCount = detail?.participants.length ?? 0;
    const isGroupChat = detail?.kind === 'group';
    const [participantsExpanded, setParticipantsExpanded] = useState(true);
    const imageGroups = useMemo(() => groupAssets(detail?.images ?? []), [detail?.images]);
    const videoGroups = useMemo(() => groupAssets(detail?.videos ?? []), [detail?.videos]);
    const fileGroups = useMemo(() => groupAssets(detail?.files ?? []), [detail?.files]);

    const [viewerAttachment, setViewerAttachment] = useState<ChatAttachment | null>(null);

    const openMediaViewer = useCallback((item: ChatDetailAttachment) => {
      setViewerAttachment(item);
    }, []);

    const closeMediaViewer = useCallback(() => {
      setViewerAttachment(null);
    }, []);

    useEffect(() => {
      if (!isOpen) {
        setViewerAttachment(null);
      }
    }, [isOpen]);

    useEffect(() => {
      if (view !== 'photos' && view !== 'videos') {
        setViewerAttachment(null);
      }
    }, [view]);

    const headerTitle =
      view === 'overview'
        ? title
        : view === 'photos'
          ? 'Photo'
          : view === 'videos'
            ? 'Video'
            : view === 'files'
              ? 'Files'
              : 'Pinned messages';

    return (
      <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        placement="center"
        hideCloseButton
        classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] bg-[#F4F4F4] shadow-[0_24px_48px_rgba(30,30,30,0.12)]`,
          body: 'p-0',
          wrapper: 'px-2',
          backdrop: 'bg-black/30',
        }}
      >
        <ModalContent>
          <ModalBody>
            <div className="relative min-h-[640px] px-6 pb-8 pt-6 sm:px-9">
              <div className="flex items-center justify-between">
                {view === 'overview' ? <div className="h-8 w-8" /> : <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#1E1E1E] transition-colors hover:bg-black/[0.04]"
                  onClick={onBack}
                  aria-label="Back"
                >
                  <IconArrowLeft className="h-5 w-5" />
                </button>}
                <h2 className="text-[22px] font-semibold leading-[110%] text-[#1E1E1E]">
                  {view === 'overview' ? '' : headerTitle}
                </h2>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#757575] transition-colors hover:bg-black/[0.04] hover:text-[#1E1E1E]"
                  onClick={onClose}
                  aria-label="Close details modal"
                >
                  <IconClose className="h-[18px] w-[18px]" />
                </button>
              </div>

              {isLoading ? (
                <div className="flex min-h-[520px] items-center justify-center text-sm text-[#7A7A7A]">
                  Loading chat details...
                </div>
              ) : errorMessage ? (
                <div className="flex min-h-[520px] items-center justify-center text-center text-sm font-medium text-[#D13939]">
                  {errorMessage}
                </div>
              ) : view === 'overview' ? (
                <div className="pb-2">
                  <div className="flex flex-col items-center pt-2 text-center">
                    <Avatar
                      name={title}
                      src={detail?.avatarUrl ?? undefined}
                      className="h-[128px] w-[128px] text-large"
                      showFallback
                    />
                    <h2 className="mt-6 text-[32px] font-semibold leading-[110%] text-[#1E1E1E]">{title}</h2>
                    <p className="mt-2 text-[16px] font-normal leading-[140%] text-[#8E8E8E]">
                      {participantCount} participants
                    </p>
                  </div>

                  <div className="mt-8 border-t border-[#DEDEDE] pt-7">
                    {pinnedMessages.length > 0 ? (
                      <MediaRow
                        icon={<IconPin className="h-5 w-5" />}
                        label={`${pinnedMessages.length} pinned message${pinnedMessages.length === 1 ? '' : 's'}`}
                        onClick={() => onOpenView('pinned')}
                      />
                    ) : null}
                    <MediaRow
                      icon={<IconImage className="h-5 w-5" />}
                      label={`${detail?.images.length ?? 0} photos`}
                      onClick={() => onOpenView('photos')}
                    />
                    <MediaRow
                      icon={<IconVideo className="h-5 w-5" />}
                      label={`${detail?.videos.length ?? 0} video`}
                      onClick={() => onOpenView('videos')}
                    />
                    <MediaRow
                      icon={<IconFile className="h-5 w-5" />}
                      label={`${detail?.files.length ?? 0} files`}
                      onClick={() => onOpenView('files')}
                    />
                  </div>

                  {isGroupChat ? (
                    <div className="mt-8 border-t border-[#DEDEDE] pt-7">
                      <button
                        type="button"
                        className="mb-4 flex w-full items-center justify-between text-[#8E8E8E]"
                        onClick={() => setParticipantsExpanded((value) => !value)}
                      >
                        <div className="flex items-center gap-2">
                          <IconUsers className="h-5 w-5" />
                          <span className="text-[16px] font-medium">
                            {participantCount} participants
                          </span>
                        </div>
                        <IconChevronDown
                          className={cn(
                            'h-5 w-5 transition-transform',
                            participantsExpanded && 'rotate-180',
                          )}
                        />
                      </button>
                      {adminToggleErrorMessage ? (
                        <p className="mb-3 text-sm text-danger">{adminToggleErrorMessage}</p>
                      ) : null}
                      {participantsExpanded ? (
                        <div className="max-h-[300px] space-y-1 overflow-y-auto pr-4">
                          {detail?.participants.map((participant) => {
                            const fullName =
                              [participant.user.firstName, participant.user.lastName]
                                .filter(Boolean)
                                .join(' ')
                                .trim() || participant.user.username;
                            const role = participant.chatRole.toLowerCase();
                            const showAdminToggle =
                              !!detail &&
                              (canToggleParticipantAdminRole(
                                detail,
                                currentUserId,
                                isStaffUser,
                                participant,
                              ) ||
                                canPromoteGroupParticipantToAdmin(
                                  detail,
                                  currentUserId,
                                  isStaffUser,
                                  participant,
                                ));
                            const showRemove =
                              !!detail &&
                              canManageParticipants &&
                              canRemoveGroupParticipant(
                                detail,
                                currentUserId,
                                isStaffUser,
                                participant,
                              ) &&
                              !!onRemoveParticipant;
                            const isToggling = togglingAdminUserId === participant.user.id;
                            const isRemoving =
                              removingParticipantUserId === participant.user.id;
                            const presence = buildUserPresenceLabel(participant.user);

                            return (
                              <div
                                key={participant.participantId}
                                className="flex items-center gap-3 rounded-2xl px-2 py-2.5"
                              >
                                <div className="relative shrink-0">
                                  <Avatar
                                    name={fullName}
                                    src={participant.user.avatarUrl ?? undefined}
                                    className="h-12 w-12 shrink-0"
                                    showFallback
                                  />
                                  {presence?.online ? (
                                    <span
                                      className="absolute right-0 bottom-0 size-2.5 rounded-full border-2 border-white bg-success"
                                      aria-hidden
                                    />
                                  ) : null}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <span className="truncate text-[16px] font-medium text-[#1E1E1E]">
                                      {fullName}
                                    </span>
                                    <ParticipantRoleBadge role={participant.chatRole} />
                                  </div>
                                  <span className="mt-0.5 block text-[13px] text-[#8B8B8B]">
                                    {getParticipantJobTitle(participant)}
                                  </span>
                                  {presence ? (
                                    <span
                                      className={cn(
                                        'mt-0.5 block text-[12px]',
                                        presence.online
                                          ? 'font-medium text-success'
                                          : 'text-[#8B8B8B]',
                                      )}
                                    >
                                      {presence.lastSeenLabel}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                  {showAdminToggle && onToggleParticipantAdmin ? (
                                    <button
                                      type="button"
                                      disabled={
                                        isToggling ||
                                        togglingAdminUserId !== null ||
                                        removingParticipantUserId !== null
                                      }
                                      onClick={() =>
                                        void onToggleParticipantAdmin(participant.user.id)
                                      }
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#757575] transition-colors hover:bg-black/[0.04] hover:text-[#1E1E1E] disabled:opacity-50"
                                      aria-label={
                                        role === 'admin' ? 'Remove admin' : 'Make admin'
                                      }
                                    >
                                      <IconUserPlus className="h-[18px] w-[18px]" />
                                    </button>
                                  ) : null}
                                  {showRemove ? (
                                    <button
                                      type="button"
                                      disabled={
                                        isRemoving ||
                                        removingParticipantUserId !== null ||
                                        togglingAdminUserId !== null
                                      }
                                      onClick={() => void onRemoveParticipant(participant.user.id)}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#757575] transition-colors hover:bg-black/[0.04] hover:text-danger disabled:opacity-50"
                                      aria-label="Remove participant"
                                    >
                                      <IconTrash className="h-[18px] w-[18px]" />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {leaveErrorMessage ? (
                    <p className="mt-3 text-center text-sm text-danger">{leaveErrorMessage}</p>
                  ) : null}
                  {deleteErrorMessage ? (
                    <p className="mt-1 text-center text-sm text-danger">{deleteErrorMessage}</p>
                  ) : null}
                </div>
              ) : view === 'pinned' ? (
                <div className="mt-8 max-h-[720px] overflow-y-auto pr-4">
                  {pinnedMessages.length ? (
                    <div className="space-y-2">
                      {pinnedMessages.map((message) => (
                        <div
                          key={`detail-pinned-${message.id}`}
                          className="rounded-[14px] bg-white px-4 py-3 shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
                        >
                          <div className="flex items-center gap-2">
                            <IconPin className="h-4 w-4 shrink-0 text-[#1E1E1E]" />
                            <p className="text-sm font-semibold text-[#1E1E1E]">{message.authorName}</p>
                          </div>
                          <p className="mt-1 line-clamp-3 text-sm leading-5 text-default-600">
                            {getPinnedPreview(message)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-sm text-[#7A7A7A]">No pinned messages</div>
                  )}
                </div>
              ) : view === 'photos' ? (
                <div className="mt-8 max-h-[720px] overflow-y-auto pr-4">
                  {imageGroups.length ? (
                    imageGroups.map((group) => (
                      <section key={group.label} className="mb-8">
                        <h3 className="mb-4 text-[16px] font-medium text-[#1E1E1E]">{group.label}</h3>
                        <div className="grid grid-cols-3 gap-3">
                          {group.items.map((item) => (
                            <button
                              key={item.key}
                              type="button"
                              className="block overflow-hidden rounded-[18px] text-left outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-primary"
                              onClick={() => openMediaViewer(item)}
                            >
                              <img
                                src={getChatAttachmentPreviewUrl(item) ?? undefined}
                                alt={item.name}
                                className="aspect-square w-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </section>
                    ))
                  ) : (
                    <div className="py-12 text-center text-sm text-[#7A7A7A]">No photos yet</div>
                  )}
                </div>
              ) : view === 'videos' ? (
                <div className="mt-8 max-h-[720px] overflow-y-auto pr-4">
                  {videoGroups.length ? (
                    videoGroups.map((group) => (
                      <section key={group.label} className="mb-8">
                        <h3 className="mb-4 text-[16px] font-medium text-[#1E1E1E]">{group.label}</h3>
                        <div className="grid grid-cols-3 gap-3">
                          {group.items.map((item) => (
                            <button
                              key={item.key}
                              type="button"
                              className="relative block overflow-hidden rounded-[18px] text-left outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-primary"
                              onClick={() => openMediaViewer(item)}
                            >
                              <ChatDetailVideoThumbnail item={item} />
                              <div className="pointer-events-none absolute inset-x-2 bottom-2 flex items-center gap-1 rounded-[8px] bg-black/45 px-2 py-1 text-white">
                                <IconPlay className="h-3.5 w-3.5" />
                              </div>
                            </button>
                          ))}
                        </div>
                      </section>
                    ))
                  ) : (
                    <div className="py-12 text-center text-sm text-[#7A7A7A]">No videos yet</div>
                  )}
                </div>
              ) : (
                <div className="mt-8 max-h-[720px] overflow-y-auto pr-4">
                  {fileGroups.length ? (
                    fileGroups.map((group) => (
                      <section key={group.label} className="mb-8">
                        <h3 className="mb-4 text-[16px] font-medium text-[#1E1E1E]">{group.label}</h3>
                        <div className="space-y-2">
                          {group.items.map((item) => (
                            <div
                              key={item.key}
                              className="flex items-center gap-4 rounded-[14px] bg-black/[0.04] px-4 py-4"
                            >
                              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] border border-[#D6D6D6] bg-white text-[#D93D31]">
                                <IconFile className="h-5 w-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-[16px] font-medium text-[#1E1E1E]">
                                  {item.name}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => downloadChatAttachment(item)}
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#27245F] transition-colors hover:bg-black/[0.04]"
                                aria-label={`Download ${item.name}`}
                              >
                                <IconDownload className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </section>
                    ))
                  ) : (
                    <div className="py-12 text-center text-sm text-[#7A7A7A]">No files yet</div>
                  )}
                </div>
              )}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
      <ChatMediaViewerModal
        isOpen={!!viewerAttachment}
        attachment={viewerAttachment}
        onClose={closeMediaViewer}
      />
      </>
    );
  },
);

ChatDetailsModal.displayName = 'ChatDetailsModal';
